import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Star,
  Sparkles,
  ShieldCheck,
  Truck,
  MessageCircle,
  ShoppingBag,
  Award,
  Layers,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  X,
  Compass,
  Eye,
  RefreshCw
} from "lucide-react";
import { listProducts, listTestimonials } from "../lib/dataStore";
import ProductCard from "../components/ProductCard";
import { buildWhatsAppInquiryUrl } from "../lib/whatsapp";
import { normaliseImageUrl } from "../lib/driveImage";

export default function Home() {
  const [allProducts, setAllProducts] = useState([]);
  const [activeCategoryFilter, setActiveCategoryFilter] = useState("All");
  const [testimonials, setTestimonials] = useState([]);

  // Interactive Hero Collection Selector State
  const [activeHeroTab, setActiveHeroTab] = useState("leather"); // "leather" | "cotton" | "artisan"

  // Automated Animated Image Carousel State
  const [heroSlideIndex, setHeroSlideIndex] = useState(0);
  const [isAutoSlide, setIsAutoSlide] = useState(true);

  // Interactive Craft Story Explorer State
  const [activeCraftStep, setActiveCraftStep] = useState(0);

  // Testimonial Lightbox State
  const [selectedTestimonialImg, setSelectedTestimonialImg] = useState(null);

  // Bags Sold State with local caching to eliminate redundant Firebase reads
  const [bagsSold, setBagsSold] = useState(() => {
    const cached = localStorage.getItem("lineacraft_bags_sold_cache");
    if (cached) {
      const parsed = parseInt(cached, 10);
      if (!isNaN(parsed) && parsed > 0) return parsed;
    }
    return 1480;
  });

  useEffect(() => {
    const lastFetch = localStorage.getItem("lineacraft_bags_sold_time");
    const now = Date.now();
    const CACHE_TTL = 30 * 60 * 1000; // 30 minutes cache

    if (!lastFetch || now - parseInt(lastFetch, 10) > CACHE_TTL) {
      try {
        const localOrders = JSON.parse(localStorage.getItem("lc_orders") || "[]");
        let itemsCount = 0;
        for (const order of localOrders) {
          if (Array.isArray(order.items)) {
            for (const item of order.items) {
              itemsCount += Number(item.quantity || 1);
            }
          } else {
            itemsCount += 1;
          }
        }
        const totalBags = 1480 + itemsCount;
        setBagsSold(totalBags);
        localStorage.setItem("lineacraft_bags_sold_cache", totalBags.toString());
        localStorage.setItem("lineacraft_bags_sold_time", now.toString());
      } catch (e) {
        console.warn("Error calculating bags sold:", e);
      }
    }
  }, []);

  const bagsSoldFormatted = `${bagsSold.toLocaleString()}+`;

  // Horizontal Scroll Ref for Product Carousel
  const productCarouselRef = useRef(null);

  const heroSlides = [
    {
      img: "/images/top_banner.jpg",
      tag: "100% Genuine Leather",
      title: "Santiniketan Embossed Leather Tote",
      subtitle: "Hand-carved batik artwork on vegetable-tanned genuine leather."
    },
    {
      img: "/images/hero/1000121094.jpeg",
      tag: "Pure Handloom Cotton",
      title: "Shantiniketan Cotton Canvas Tote",
      subtitle: "Lightweight, breathable eco-canvas hand-printed in Bolpur."
    },
    {
      img: "/images/product_banner.png",
      tag: "Master Artisan Collection",
      title: "Bolpur Workshop Masterpiece",
      subtitle: "Every stitch celebrates Bengal's rich leathercraft tradition."
    },
    {
      img: "/images/hero/1000232045.png",
      tag: "Heritage Craft",
      title: "Traditional Batik Work Bag",
      subtitle: "Reinforced structure with rich organic dye hand-finishing."
    }
  ];

  // Automated Slideshow Timer
  useEffect(() => {
    if (!isAutoSlide) return;
    const interval = setInterval(() => {
      setHeroSlideIndex((prev) => (prev + 1) % heroSlides.length);
    }, 3800);
    return () => clearInterval(interval);
  }, [isAutoSlide, heroSlides.length]);

  useEffect(() => {
    listProducts().then((p) => {
      setAllProducts(p);
    });
    listTestimonials().then(setTestimonials);
  }, []);

  // Filtered products for Featured Section
  const filteredProducts = allProducts.filter((p) => {
    if (activeCategoryFilter === "All") return true;
    if (activeCategoryFilter === "Leather") {
      return (
        p.category?.toLowerCase().includes("leather") ||
        p.name?.toLowerCase().includes("leather")
      );
    }
    if (activeCategoryFilter === "Cotton") {
      return (
        p.category?.toLowerCase().includes("cotton") ||
        p.name?.toLowerCase().includes("cotton") ||
        p.category?.toLowerCase().includes("tote")
      );
    }
    return true;
  });

  // Hero Collection Content Mapping
  const heroContent = {
    leather: {
      tag: "Santiniketan Leather • Finish Leather",
      title: "Handcrafted Leather Elegance",
      subtitle:
        "Engineered with vegetable-tanned genuine leather bags from Bolpur artisans.",
      floatingCardImg: "/images/hero/1000232045.png",
      floatingCardTag: "Heritage Leather",
      floatingCardTitle: "Batik Work Tote"
    },
    cotton: {
      tag: "Eco Handloom • Vibrant Weaves",
      title: "Artisanal Cotton Bags",
      subtitle:
        "Breathable, lightweight cotton with timeless folk patterns for your daily lifestyle.",
      floatingCardImg: "/images/hero/1000121094.jpeg",
      floatingCardTag: "Pure Cotton",
      floatingCardTitle: "Shantiniketan Tote"
    },
    artisan: {
      tag: "Handcrafted • Limited Editions",
      title: "Master Craftsmanship",
      subtitle:
        "Every stitch tells a story of heritage, patience, and meticulous dedication passed down across generations.",
      floatingCardImg: "/images/top_banner.jpg",
      floatingCardTag: "Studio Edition",
      floatingCardTitle: "Artisan Bag Collection"
    }
  };

  const currentHero = heroContent[activeHeroTab];

  // Horizontal Scroll Handler
  const scrollProductCarousel = (direction) => {
    if (productCarouselRef.current) {
      const scrollAmount = direction === "left" ? -320 : 320;
      productCarouselRef.current.scrollBy({
        left: scrollAmount,
        behavior: "smooth"
      });
    }
  };

  // Craft Journey Steps
  const craftSteps = [
    {
      num: "01",
      title: "Ethical Raw Sourcing",
      desc: "Selecting full-grain vegetable tanned hides & organic cotton directly from local sustainable mills.",
      detail: "No harsh chemicals are used; only natural tree barks and eco dyes."
    },
    {
      num: "02",
      title: "Artisanal Batik Embossing",
      desc: "Skilled Santiniketan artisans hand-engrave traditional motifs using heated brass tools.",
      detail: "A century-old artistic tradition championed in Bolpur, West Bengal."
    },
    {
      num: "03",
      title: "Precision Stitching",
      desc: "Reinforced edges, high-grade brass hardware, and heavy-duty thread for lifelong durability.",
      detail: "Every stress point is double-stitched for active everyday use."
    },
    {
      num: "04",
      title: "Quality Test Before Shipping",
      desc: "Every bag undergoes a strict multi-point quality test & durability inspection before dispatch.",
      detail: "Inspected for stitching integrity, hardware strength, and flawless finishing."
    }
  ];

  return (
    <div className="relative min-h-screen bg-background overflow-x-hidden selection:bg-terracotta selection:text-white">
      {/* Fixed Floating "Shop Now" Action Button (Positioned cleanly on Bottom-Left to avoid header/WhatsApp collisions) */}
      <Link
        to="/shop"
        data-testid="floating-shop-now-btn"
        className="fixed bottom-6 left-5 sm:bottom-8 sm:left-6 z-50 bg-terracotta text-white px-5 py-3 rounded-full shadow-2xl hover:bg-terracotta/90 hover:scale-110 active:scale-95 transition-all duration-300 font-semibold text-xs sm:text-sm flex items-center gap-2.5 border-2 border-white/40 glow-button backdrop-blur-md"
      >
        <ShoppingBag className="w-4 h-4 animate-bounce" />
        <span>Shop Now</span>
        <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
      </Link>

      {/* Floating WhatsApp Action Button (Positioned on Bottom-Right) */}
      <a
        href={buildWhatsAppInquiryUrl(
          "Hi Lineacraft India, I'm visiting your website and would love to know more about your handcrafted products."
        )}
        target="_blank"
        rel="noreferrer"
        data-testid="floating-whatsapp-btn"
        className="fixed bottom-6 right-5 sm:bottom-8 sm:right-6 z-50 bg-[#25D366] text-white p-3.5 sm:px-5 sm:py-3.5 rounded-full shadow-2xl hover:bg-[#20ba5a] hover:scale-110 active:scale-95 transition-all duration-300 flex items-center gap-2 text-xs sm:text-sm font-semibold border-2 border-white/40"
        aria-label="Chat on WhatsApp"
      >
        <div className="relative">
          <MessageCircle className="w-5 h-5 fill-current" />
          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-emerald-300 rounded-full border border-white animate-pulse" />
        </div>
        <span className="hidden sm:inline">WhatsApp Us</span>
      </a>

      {/* ========================================================================= */}
      {/* HERO SECTION (GOLDEN RATIO + AUTOMATED IMAGE SLIDESHOW)                   */}
      {/* ========================================================================= */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#f8f4ef] via-[#fcfaf7] to-background pt-6 pb-16 lg:py-20 border-b border-border/40">
        <div className="absolute inset-0 pointer-events-none opacity-40 grain" />
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-terracotta/10 rounded-full blur-3xl pointer-events-none float" />
        <div className="absolute top-1/2 -right-32 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl pointer-events-none float-reverse" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          {/* Live Studio Micro Banner */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6 bg-white/80 backdrop-blur-md px-4 py-2 rounded-full border border-border/60 max-w-xl mx-auto lg:mx-0 shadow-sm">
            <div className="flex items-center gap-2 text-xs font-medium text-foreground">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
              <span>Artisans active in Bolpur, West Bengal</span>
            </div>
            <Link
              to="/shop"
              className="text-[11px] text-terracotta font-semibold uppercase tracking-wider hover:underline flex items-center gap-1"
            >
              <Sparkles className="w-3.5 h-3.5" /> Shop Handcrafted Range →
            </Link>
          </div>

          {/* Golden Ratio Grid Layout */}
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left Content Column (~61.8% visual weight) */}
            <div className="lg:col-span-7 text-center lg:text-left space-y-6">
              {/* Interactive Material Filter Tabs */}
              <div className="inline-flex items-center gap-1.5 p-1.5 bg-muted/80 backdrop-blur-md rounded-full border border-border/80 shadow-inner">
                <button
                  type="button"
                  data-testid="hero-tab-leather"
                  onClick={() => setActiveHeroTab("leather")}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-all duration-300 flex items-center gap-1.5 cursor-pointer ${
                    activeHeroTab === "leather"
                      ? "bg-terracotta text-white shadow-md scale-105"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Award className="w-3.5 h-3.5" /> Leather Heritage
                </button>
                <button
                  type="button"
                  data-testid="hero-tab-cotton"
                  onClick={() => setActiveHeroTab("cotton")}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-all duration-300 flex items-center gap-1.5 cursor-pointer ${
                    activeHeroTab === "cotton"
                      ? "bg-terracotta text-white shadow-md scale-105"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Layers className="w-3.5 h-3.5" /> Artisanal Cotton
                </button>
                <button
                  type="button"
                  data-testid="hero-tab-artisan"
                  onClick={() => setActiveHeroTab("artisan")}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-all duration-300 flex items-center gap-1.5 cursor-pointer ${
                    activeHeroTab === "artisan"
                      ? "bg-terracotta text-white shadow-md scale-105"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Compass className="w-3.5 h-3.5" /> Studio Crafts
                </button>
              </div>

              <div>
                <span className="inline-block px-3.5 py-1 rounded-full bg-terracotta/10 text-terracotta text-xs font-semibold tracking-widest uppercase border border-terracotta/20 mb-3">
                  {currentHero.tag}
                </span>

                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif leading-[1.1] tracking-tight text-foreground transition-all duration-500">
                  {currentHero.title.split(" ")[0]} <br className="hidden sm:inline" />
                  <span className="text-terracotta italic font-normal">
                    {currentHero.title.split(" ").slice(1).join(" ")}
                  </span>
                </h1>
              </div>

              <p className="text-base sm:text-lg leading-relaxed text-muted-foreground max-w-xl mx-auto lg:mx-0">
                {currentHero.subtitle}
              </p>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 pt-2">
                <Link
                  to="/shop"
                  data-testid="hero-explore-btn"
                  className="w-full sm:w-auto px-8 py-3.5 rounded-full bg-terracotta text-white font-medium text-center hover:bg-terracotta/90 hover:shadow-xl hover:scale-105 active:scale-95 transition-all duration-300 flex items-center justify-center gap-2 group glow-button"
                >
                  <ShoppingBag className="w-4 h-4" />
                  <span>Shop Collection Now</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>

                <a
                  href={buildWhatsAppInquiryUrl(
                    `Hi Lineacraft India, I want to inquire about your ${currentHero.floatingCardTitle}.`
                  )}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="hero-whatsapp-btn"
                  className="w-full sm:w-auto px-7 py-3.5 rounded-full bg-white border border-border/80 text-foreground font-medium text-center hover:border-emerald-500 hover:text-emerald-700 hover:bg-emerald-50/50 hover:scale-105 active:scale-95 transition-all duration-300 flex items-center justify-center gap-2 shadow-sm"
                >
                  <MessageCircle className="w-4 h-4 text-[#25D366] fill-current" />
                  <span>Custom Inquiry</span>
                </a>
              </div>

              {/* Quick Feature Badges */}
              <div className="grid grid-cols-3 gap-3 pt-6 border-t border-border/60 text-center lg:text-left">
                <div className="p-3 rounded-2xl bg-white/60 border border-border/50 hover:border-terracotta/40 transition-colors">
                  <div className="flex items-center justify-center lg:justify-start gap-1.5 text-terracotta font-numeric text-xl font-extrabold tracking-tight">
                    <span>100%</span>
                  </div>
                  <div className="text-xs font-medium text-foreground">Handcrafted</div>
                  <div className="text-[10px] text-muted-foreground">Bolpur Artisans</div>
                </div>

                <div className="p-3 rounded-2xl bg-white/60 border border-border/50 hover:border-terracotta/40 transition-colors">
                  <div className="flex items-center justify-center lg:justify-start gap-1.5 text-terracotta font-numeric text-xl font-extrabold tracking-tight">
                    <span>{bagsSoldFormatted}</span>
                  </div>
                  <div className="text-xs font-medium text-foreground">Bags Sold</div>
                  <div className="text-[10px] text-muted-foreground">Crafted & Delivered</div>
                </div>

                <div className="p-3 rounded-2xl bg-white/60 border border-border/50 hover:border-terracotta/40 transition-colors">
                  <div className="flex items-center justify-center lg:justify-start gap-1.5 text-terracotta font-numeric text-xl font-extrabold tracking-tight">
                    <span>Fast</span>
                  </div>
                  <div className="text-xs font-medium text-foreground">Pan-India Ship</div>
                  <div className="text-[10px] text-muted-foreground">Direct to Doorstep</div>
                </div>
              </div>
            </div>

            {/* Right Media Column (~38.2% visual weight, Automated Image Slideshow) */}
            <div
              className="lg:col-span-5 relative"
              onMouseEnter={() => setIsAutoSlide(false)}
              onMouseLeave={() => setIsAutoSlide(true)}
            >
              <div className="relative mx-auto max-w-md lg:max-w-none">
                {/* Rotating Seal Badge */}
                <div className="absolute -top-6 -left-6 z-30 hidden sm:flex items-center justify-center w-24 h-24 rounded-full bg-terracotta text-white p-2 shadow-xl border-2 border-white spin-slow">
                  <svg viewBox="0 0 100 100" className="w-full h-full fill-current">
                    <path id="circlePath" d="M 50, 50 m -35, 0 a 35,35 0 1,1 70,0 a 35,35 0 1,1 -70,0" fill="none" />
                    <text className="text-[9px] uppercase font-bold tracking-[2.8px]">
                      <textPath href="#circlePath">
                        LINEACRAFT • • • INDIA • • • 
                      </textPath>
                    </text>
                  </svg>
                  <Award className="absolute w-7 h-7 text-amber-200" />
                </div>

                {/* Main Hero Slideshow Container with Smooth Animated Image Changing */}
                <div className="relative rounded-[32px] overflow-hidden shadow-2xl bg-white border-4 border-white group h-[380px] sm:h-[480px]">
                  {heroSlides.map((slide, idx) => (
                    <div
                      key={idx}
                      className={`absolute inset-0 transition-all duration-700 ease-in-out ${
                        idx === heroSlideIndex
                          ? "opacity-100 scale-100 z-10"
                          : "opacity-0 scale-105 pointer-events-none z-0"
                      }`}
                    >
                      <img
                        src={slide.img}
                        alt={slide.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent flex flex-col justify-end p-6 text-white">
                        <span className="px-3 py-1 bg-terracotta text-white text-[11px] font-semibold uppercase tracking-wider rounded-full w-max mb-2 shadow">
                          {slide.tag}
                        </span>
                        <h2 className="font-serif text-2xl font-bold leading-tight drop-shadow-md">
                          {slide.title}
                        </h2>
                        <p className="text-xs text-white/90 mt-1 line-clamp-2">
                          {slide.subtitle}
                        </p>
                      </div>
                    </div>
                  ))}

                  {/* Manual Arrow Controls */}
                  <button
                    type="button"
                    onClick={() =>
                      setHeroSlideIndex((prev) => (prev - 1 + heroSlides.length) % heroSlides.length)
                    }
                    className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full bg-black/40 text-white hover:bg-black/70 flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 cursor-pointer"
                    aria-label="Previous image"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      setHeroSlideIndex((prev) => (prev + 1) % heroSlides.length)
                    }
                    className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full bg-black/40 text-white hover:bg-black/70 flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 cursor-pointer"
                    aria-label="Next image"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>

                  {/* Indicator Dots */}
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/40 backdrop-blur-md">
                    {heroSlides.map((_, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setHeroSlideIndex(idx)}
                        className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${
                          idx === heroSlideIndex ? "w-6 bg-terracotta" : "w-2 bg-white/60 hover:bg-white"
                        }`}
                        aria-label={`Slide ${idx + 1}`}
                      />
                    ))}
                  </div>
                </div>

                {/* Secondary Floating Card */}
                <div className="absolute -bottom-8 -left-4 sm:-left-8 z-20 w-64 rounded-2xl bg-white/95 backdrop-blur-md p-3.5 shadow-2xl border border-border/80 hover:-translate-y-2 transition-all duration-300 hidden sm:block float">
                  <div className="flex gap-3 items-center">
                    <img
                      src={currentHero.floatingCardImg}
                      alt={currentHero.floatingCardTitle}
                      className="w-16 h-16 rounded-xl object-cover border border-border shrink-0"
                    />
                    <div className="min-w-0">
                      <span className="text-[10px] font-semibold uppercase tracking-widest text-terracotta block">
                        {currentHero.floatingCardTag}
                      </span>
                      <h3 className="font-serif text-sm font-bold text-foreground truncate">
                        {currentHero.floatingCardTitle}
                      </h3>
                      <div className="flex items-center gap-1 text-[11px] text-emerald-700 font-semibold mt-0.5">
                        <CheckCircle2 className="w-3 h-3" /> Hand-Finished
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* CONTINUOUS ANIMATED HORIZONTAL MARQUEE STRIP                             */}
      {/* ========================================================================= */}
      <div className="bg-terracotta text-white py-3.5 overflow-hidden relative shadow-inner">
        <div className="flex animate-scroll whitespace-nowrap gap-8 text-xs sm:text-sm font-medium tracking-wide">
          {[...Array(3)].map((_, loopIdx) => (
            <div key={loopIdx} className="flex items-center gap-8 shrink-0">
              <span className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-200" />
                <span>100% Genuine Santiniketan Leather</span>
              </span>
              <span>•</span>
              <span className="flex items-center gap-2">
                <Award className="w-4 h-4 text-amber-200" />
                <span>Hand-Embossed Traditional Batik Art</span>
              </span>
              <span>•</span>
              <span className="flex items-center gap-2">
                <Layers className="w-4 h-4 text-amber-200" />
                <span>Eco Natural Handloom Cotton Bags</span>
              </span>
              <span>•</span>
              <span className="flex items-center gap-2">
                <Truck className="w-4 h-4 text-amber-200" />
                <span>Pan-India Shipping Direct to Your Doorstep</span>
              </span>
              <span>•</span>
            </div>
          ))}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* HORIZONTAL SCROLLING FEATURED PRODUCTS CAROUSEL                           */}
      {/* ========================================================================= */}
      <section className="py-16 sm:py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-8 gap-4">
          <div>
            <span className="overline text-terracotta">Featured Products</span>
            <h2 className="text-3xl sm:text-4xl font-serif text-foreground mt-1">
              Curated Handcrafted Collection
            </h2>
          </div>

          <div className="flex items-center gap-3">
            {/* Category Quick Filter Pills */}
            <div className="flex items-center gap-1.5 p-1 bg-muted rounded-full border border-border">
              {["All", "Leather", "Cotton"].map((cat) => (
                <button
                  key={cat}
                  type="button"
                  data-testid={`filter-btn-${cat}`}
                  onClick={() => setActiveCategoryFilter(cat)}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-all duration-300 cursor-pointer ${
                    activeCategoryFilter === cat
                      ? "bg-terracotta text-white shadow"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Horizontal Scroll Arrows */}
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                data-testid="carousel-scroll-left"
                onClick={() => scrollProductCarousel("left")}
                className="w-9 h-9 rounded-full border border-border bg-white text-foreground hover:bg-terracotta hover:text-white hover:border-terracotta transition-colors flex items-center justify-center shadow-sm cursor-pointer"
                aria-label="Scroll left"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                type="button"
                data-testid="carousel-scroll-right"
                onClick={() => scrollProductCarousel("right")}
                className="w-9 h-9 rounded-full border border-border bg-white text-foreground hover:bg-terracotta hover:text-white hover:border-terracotta transition-colors flex items-center justify-center shadow-sm cursor-pointer"
                aria-label="Scroll right"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Smooth Horizontal Carousel Container */}
        <div
          ref={productCarouselRef}
          className="flex gap-4 sm:gap-6 overflow-x-auto no-scrollbar scroll-smooth pb-4 pt-2 -mx-4 px-4 sm:-mx-6 sm:px-6"
        >
          {filteredProducts.map((p, i) => (
            <div
              key={p.id}
              className="w-[260px] sm:w-[290px] shrink-0 transition-transform duration-300 hover:-translate-y-2"
            >
              <ProductCard product={p} index={i} />
            </div>
          ))}
        </div>

        <div className="mt-8 text-center">
          <Link
            to="/shop"
            className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-foreground text-background font-medium text-xs sm:text-sm hover:bg-terracotta hover:text-white transition-all shadow-md"
          >
            <span>Explore All Products</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* INTERACTIVE CRAFT PROCESS JOURNEY (4-STEP TIMELINE)                       */}
      {/* ========================================================================= */}
      <section className="py-16 sm:py-24 bg-[#f8f4ef] border-y border-border/60 grain relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
            <div>
              <span className="overline text-terracotta">Craftsmanship</span>
              <h2 className="text-3xl sm:text-4xl font-serif tracking-tight mt-1 text-foreground">
                How Your Bag Is Handcrafted
              </h2>
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              Tap any step below to explore the traditional techniques used in our Bolpur workshop.
            </p>
          </div>

          {/* Step Selector Pills */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {craftSteps.map((step, idx) => (
              <button
                key={idx}
                type="button"
                data-testid={`craft-step-${idx}`}
                onClick={() => setActiveCraftStep(idx)}
                className={`p-4 rounded-2xl text-left border transition-all duration-300 cursor-pointer ${
                  activeCraftStep === idx
                    ? "bg-white border-terracotta shadow-xl scale-105"
                    : "bg-white/60 border-border hover:bg-white/90"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-numeric text-xl font-extrabold text-terracotta tracking-tight">
                    {step.num}
                  </span>
                  {activeCraftStep === idx && (
                    <span className="w-2 h-2 rounded-full bg-terracotta animate-ping" />
                  )}
                </div>
                <h3 className="font-serif text-sm font-bold text-foreground">
                  {step.title}
                </h3>
              </button>
            ))}
          </div>

          {/* Step Detail Spotlight */}
          <div className="bg-white rounded-3xl p-6 sm:p-10 border border-border shadow-xl grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-7 space-y-4">
              <span className="px-3 py-1 rounded-full bg-terracotta/10 text-terracotta text-xs font-semibold uppercase tracking-wider">
                Step <span className="font-numeric font-bold">{craftSteps[activeCraftStep].num}</span> Spotlight
              </span>
              <h3 className="text-2xl sm:text-3xl font-serif font-bold text-foreground">
                {craftSteps[activeCraftStep].title}
              </h3>
              <p className="text-base text-muted-foreground leading-relaxed">
                {craftSteps[activeCraftStep].desc}
              </p>
              <div className="p-4 rounded-2xl bg-muted/40 border border-border/80 text-xs sm:text-sm text-foreground flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-terracotta shrink-0 mt-0.5" />
                <div>
                  <span className="font-semibold block mb-0.5">Artisan Insight:</span>
                  <span className="text-muted-foreground">{craftSteps[activeCraftStep].detail}</span>
                </div>
              </div>
            </div>

            <div className="md:col-span-5 relative h-64 rounded-2xl overflow-hidden shadow-md">
              <img
                src="/images/product_banner.png"
                alt="Artisan at work"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-end p-4">
                <span className="text-white text-xs font-medium flex items-center gap-1">
                  <Award className="w-4 h-4 text-amber-300" /> Santiniketan Artisans at Work
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* TESTIMONIALS & CUSTOMER FEEDBACK HORIZONTAL GALLERY                       */}
      {/* ========================================================================= */}
      <section className="py-16 sm:py-24 bg-cream overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-10">
            <div>
              <span className="overline text-terracotta">Verified Customer Feedback</span>
              <h2 className="text-3xl sm:text-4xl font-serif text-foreground mt-1">
                Real Customer Photos
              </h2>
            </div>
          </div>

          {/* Continuous Animated Horizontal Scrolling Gallery */}
          <div className="overflow-hidden relative py-4">
            <div className="flex animate-scroll gap-6 w-max">
              {[...testimonials, ...testimonials].map((t, i) => (
                <div
                  key={i}
                  onClick={() => setSelectedTestimonialImg(normaliseImageUrl(t.image))}
                  className="flex-shrink-0 w-[280px] sm:w-[320px] rounded-2xl overflow-hidden shadow-md bg-white border border-border/80 group cursor-pointer hover:shadow-2xl hover:-translate-y-2 transition-all duration-300"
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-muted">
                    <img
                      src={normaliseImageUrl(t.image)}
                      alt={`Customer photo ${i + 1}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="px-3.5 py-2 rounded-full bg-white/95 text-foreground text-xs font-semibold flex items-center gap-1.5 shadow-lg">
                        <Eye className="w-4 h-4 text-terracotta" /> Tap to View Photo
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox Modal for Testimonial Photo */}
      {selectedTestimonialImg && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
          <div className="relative max-w-2xl w-full bg-card rounded-3xl overflow-hidden shadow-2xl border border-border">
            <button
              type="button"
              onClick={() => setSelectedTestimonialImg(null)}
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <img
              src={selectedTestimonialImg}
              alt="Customer Feedback"
              className="w-full h-auto max-h-[80vh] object-contain bg-black"
            />
            <div className="p-4 text-center bg-card">
              <span className="text-xs font-semibold text-terracotta uppercase tracking-wider">
                Lineacraft Verified Customer Photo
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
